// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/OrdemServicoForm.tsx
//  Página de lançamento / edição de Ordem de Serviço — inspirada no frm_dav_os.cs
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Package, CreditCard,
  User, ClipboardList, Wrench, HardDrive, Users, Activity,
  AlertTriangle, ListChecks, Printer,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ordemServicoService }           from '@/features/operacional/services/ordemServicoService';
import {
  useOsEquipamentos, useOsTecnicos, useOsAcompanhamentos,
  useOsOcorrencias, useOsTarefas, useOsDespesas,
} from '@/features/operacional/hooks/useOrdemServicoDetalhe';
import { useStatusOsCombo }              from '@/features/operacional/hooks/useStatusOs';
import { usePlanoContas }                from '@/features/contabil/hooks/usePlanoContas';
import { useContasDespesas }             from '@/features/contabil/hooks/useContasDespesas';
import { useProdutoSearch }              from '@/features/produtos/hooks/useProdutos';
import { useFormasPagamentoCombo }       from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { PessoaSearchCombobox }          from '@/components/PessoaSearchCombobox';
import { getApiErrorMessage }            from '@/lib/apiClient';
import { useAuth }                       from '@/hooks/useAuth';

import { Button }                        from '@/components/ui/button';
import { Input }                         from '@/components/ui/input';
import { Label }                         from '@/components/ui/label';
import { Textarea }                      from '@/components/ui/textarea';
import { Badge }                         from '@/components/ui/badge';
import { Checkbox }                      from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { OsImageSlot } from './OsImageSlot';

import type {
  CriarOsRequest, OsServicoRequest, DavProdutoRequest,
  OsEquipamentoRequest, OsTecnicoRequest,
} from '@/features/operacional/types';

// ── Tipos internos ────────────────────────────────────────────────────────

interface ItemServico {
  key: number;
  servicoId: number | '';
  descricaoServico: string;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
}

interface ItemProduto {
  key: number;
  produtoId: number | '';
  termoBusca: string;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
}

const SERVICO_VAZIO = (key: number): ItemServico => ({
  key, servicoId: '', descricaoServico: '',
  qtdMovimento: 1, valorUnitario: 0, valorDesconto: 0, valorAcrescimo: 0,
});

const PRODUTO_VAZIO = (key: number): ItemProduto => ({
  key, produtoId: '', termoBusca: '',
  qtdMovimento: 1, valorUnitario: 0, valorDesconto: 0, valorAcrescimo: 0,
});

let _keySeq = 1;
const nextKey = () => ++_keySeq;

// ── Componente: linha de produto com busca ────────────────────────────────

interface ProdutoLinhaProps {
  item: ItemProduto;
  onChange: (campo: keyof ItemProduto, valor: string | number) => void;
  onRemove: () => void;
  showRemove: boolean;
}

function ProdutoLinha({ item, onChange, onRemove, showRemove }: ProdutoLinhaProps) {
  const [open, setOpen] = useState(false);
  const buscaHabilitada = item.termoBusca.trim().length >= 2;
  const { data: resultados = [] } = useProdutoSearch(item.termoBusca, buscaHabilitada);

  const subtotal =
    item.qtdMovimento * item.valorUnitario - item.valorDesconto + item.valorAcrescimo;

  return (
    <TableRow>
      <TableCell className="min-w-[220px]">
        <div className="relative">
          <Input
            value={item.termoBusca}
            placeholder="Buscar produto..."
            onChange={(e) => {
              onChange('termoBusca', e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {open && resultados.length > 0 && (
            <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded shadow-md max-h-48 overflow-y-auto">
              {resultados.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                  onMouseDown={() => {
                    onChange('produtoId',   p.id);
                    onChange('termoBusca',  p.descricao);
                    onChange('valorUnitario', p.precoPadrao ?? 0);
                    setOpen(false);
                  }}
                >
                  {p.codigoBarras && <span className="text-muted-foreground mr-2">{p.codigoBarras}</span>}
                  {p.descricao}
                </button>
              ))}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="w-20">
        <Input type="number" min={0.001} step={0.001} value={item.qtdMovimento}
          onChange={(e) => onChange('qtdMovimento', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="w-28">
        <Input type="number" min={0} step={0.01} value={item.valorUnitario}
          onChange={(e) => onChange('valorUnitario', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="w-24">
        <Input type="number" min={0} step={0.01} value={item.valorDesconto}
          onChange={(e) => onChange('valorDesconto', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="w-24">
        <Input type="number" min={0} step={0.01} value={item.valorAcrescimo}
          onChange={(e) => onChange('valorAcrescimo', Number(e.target.value))} className="text-right" />
      </TableCell>
      <TableCell className="text-right font-medium w-28">
        {subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </TableCell>
      <TableCell className="w-10">
        {showRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Aba: Equipamentos recebidos ────────────────────────────────────────────

interface LinhaEquip {
  key: number;
  equipamento: string;
  numeroSerie: string;
  patrimonio: string;
  certificadoGarantia: string;
  osGarantia: string;
  licitacao: string;
}

function OsEquipamentosTab({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { equipamentos, salvarEquipamentos, isSaving } = useOsEquipamentos(osId);
  const [linhas, setLinhas] = useState<LinhaEquip[]>([]);

  useEffect(() => {
    setLinhas(equipamentos.map((e) => ({
      key: nextKey(),
      equipamento: e.equipamento ?? '',
      numeroSerie: e.numeroSerie ?? '',
      patrimonio: e.patrimonio ?? '',
      certificadoGarantia: e.certificadoGarantia ?? '',
      osGarantia: e.osGarantia ?? '',
      licitacao: e.licitacao ?? '',
    })));
  }, [equipamentos]);

  const add = () => setLinhas((p) => [...p, {
    key: nextKey(), equipamento: '', numeroSerie: '', patrimonio: '',
    certificadoGarantia: '', osGarantia: '', licitacao: '',
  }]);
  const upd = (key: number, campo: keyof LinhaEquip, valor: string) =>
    setLinhas((p) => p.map((l) => l.key === key ? { ...l, [campo]: valor } : l));
  const rem = (key: number) => setLinhas((p) => p.filter((l) => l.key !== key));

  const salvar = () => {
    const itens: OsEquipamentoRequest[] = linhas
      .filter((l) => l.equipamento.trim() !== '' || l.numeroSerie.trim() !== '')
      .map((l) => ({
        equipamento: l.equipamento || null,
        numeroSerie: l.numeroSerie || null,
        patrimonio: l.patrimonio || null,
        certificadoGarantia: l.certificadoGarantia || null,
        osGarantia: l.osGarantia || null,
        licitacao: l.licitacao || null,
      }));
    salvarEquipamentos(itens);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Equipamentos recebidos</CardTitle>
          {!disabled && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-2" onClick={add}>
                <Plus className="h-4 w-4" />Adicionar equipamento
              </Button>
              <Button size="sm" className="gap-2" onClick={salvar} disabled={isSaving}>
                <Save className="h-4 w-4" />Salvar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {linhas.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhum equipamento. Clique em "Adicionar equipamento".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipamento</TableHead>
                  <TableHead>Nº Série</TableHead>
                  <TableHead>Patrimônio</TableHead>
                  <TableHead>Cert. Garantia</TableHead>
                  <TableHead>OS Garantia</TableHead>
                  <TableHead>Licitação/Controle</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell className="min-w-[200px]">
                      <Input value={l.equipamento} disabled={disabled}
                        onChange={(e) => upd(l.key, 'equipamento', e.target.value)} />
                    </TableCell>
                    <TableCell><Input value={l.numeroSerie} disabled={disabled}
                      onChange={(e) => upd(l.key, 'numeroSerie', e.target.value)} /></TableCell>
                    <TableCell><Input value={l.patrimonio} disabled={disabled}
                      onChange={(e) => upd(l.key, 'patrimonio', e.target.value)} /></TableCell>
                    <TableCell><Input value={l.certificadoGarantia} disabled={disabled}
                      onChange={(e) => upd(l.key, 'certificadoGarantia', e.target.value)} /></TableCell>
                    <TableCell><Input value={l.osGarantia} disabled={disabled}
                      onChange={(e) => upd(l.key, 'osGarantia', e.target.value)} /></TableCell>
                    <TableCell><Input value={l.licitacao} disabled={disabled}
                      onChange={(e) => upd(l.key, 'licitacao', e.target.value)} /></TableCell>
                    <TableCell className="w-10">
                      {!disabled && (
                        <Button size="sm" variant="ghost" onClick={() => rem(l.key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Aba: Técnicos executores ───────────────────────────────────────────────

interface LinhaTecnico {
  key: number;
  tecnicoId: number | null;
  servicoId: number | '';
  qtdExecutada: number;
  valorBase: number;
  perComissao: number;
  valorComissao: number;
}

function OsTecnicosTab({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { tecnicos, salvarTecnicos, isSaving } = useOsTecnicos(osId);
  const [linhas, setLinhas] = useState<LinhaTecnico[]>([]);

  useEffect(() => {
    setLinhas(tecnicos.map((t) => ({
      key: nextKey(),
      tecnicoId: t.tecnicoId,
      servicoId: t.servicoId,
      qtdExecutada: t.qtdExecutada ?? 0,
      valorBase: t.valorBase ?? 0,
      perComissao: t.perComissao ?? 0,
      valorComissao: t.valorComissao ?? 0,
    })));
  }, [tecnicos]);

  const add = () => setLinhas((p) => [...p, {
    key: nextKey(), tecnicoId: null, servicoId: '',
    qtdExecutada: 1, valorBase: 0, perComissao: 0, valorComissao: 0,
  }]);
  const upd = (key: number, campo: keyof LinhaTecnico, valor: number | null | '') =>
    setLinhas((p) => p.map((l) => l.key === key ? { ...l, [campo]: valor } : l));
  const rem = (key: number) => setLinhas((p) => p.filter((l) => l.key !== key));

  const totalComissao = (l: LinhaTecnico) => l.valorComissao * l.qtdExecutada;

  const salvar = () => {
    const itens: OsTecnicoRequest[] = linhas
      .filter((l) => l.tecnicoId && l.servicoId !== '')
      .map((l) => ({
        tecnicoId: Number(l.tecnicoId),
        servicoId: Number(l.servicoId),
        qtdExecutada: l.qtdExecutada,
        valorBase: l.valorBase,
        perComissao: l.perComissao,
        valorComissao: l.valorComissao,
        totalComissao: totalComissao(l),
      }));
    salvarTecnicos(itens);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Técnicos executores</CardTitle>
          {!disabled && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-2" onClick={add}>
                <Plus className="h-4 w-4" />Adicionar técnico
              </Button>
              <Button size="sm" className="gap-2" onClick={salvar} disabled={isSaving}>
                <Save className="h-4 w-4" />Salvar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {linhas.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhum técnico vinculado. Clique em "Adicionar técnico".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px]">Técnico</TableHead>
                  <TableHead>Cód. Serviço</TableHead>
                  <TableHead className="text-right">Qtd Exec.</TableHead>
                  <TableHead className="text-right">Vlr Base</TableHead>
                  <TableHead className="text-right">% Comissão</TableHead>
                  <TableHead className="text-right">R$ Comissão</TableHead>
                  <TableHead className="text-right">Total Comissão</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell className="min-w-[220px]">
                      <PessoaSearchCombobox
                        value={l.tecnicoId}
                        onChange={(v) => upd(l.key, 'tecnicoId', v)}
                        placeholder="Buscar técnico..."
                        disabled={disabled}
                      />
                    </TableCell>
                    <TableCell className="w-28">
                      <Input type="number" min={1} value={l.servicoId === '' ? '' : l.servicoId}
                        placeholder="ID serviço" disabled={disabled}
                        onChange={(e) => upd(l.key, 'servicoId', e.target.value === '' ? '' : Number(e.target.value))} />
                    </TableCell>
                    <TableCell className="w-24">
                      <Input type="number" min={0} step={0.01} value={l.qtdExecutada} disabled={disabled}
                        className="text-right"
                        onChange={(e) => upd(l.key, 'qtdExecutada', Number(e.target.value))} /></TableCell>
                    <TableCell className="w-28">
                      <Input type="number" min={0} step={0.01} value={l.valorBase} disabled={disabled}
                        className="text-right"
                        onChange={(e) => upd(l.key, 'valorBase', Number(e.target.value))} /></TableCell>
                    <TableCell className="w-24">
                      <Input type="number" min={0} step={0.01} value={l.perComissao} disabled={disabled}
                        className="text-right"
                        onChange={(e) => upd(l.key, 'perComissao', Number(e.target.value))} /></TableCell>
                    <TableCell className="w-28">
                      <Input type="number" min={0} step={0.01} value={l.valorComissao} disabled={disabled}
                        className="text-right"
                        onChange={(e) => upd(l.key, 'valorComissao', Number(e.target.value))} /></TableCell>
                    <TableCell className="text-right font-medium w-28">
                      {totalComissao(l).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell className="w-10">
                      {!disabled && (
                        <Button size="sm" variant="ghost" onClick={() => rem(l.key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Aba: Status / Acompanhamento ────────────────────────────────────────────

function OsStatusTab({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { acompanhamentos, adicionarAcompanhamento, isSaving } = useOsAcompanhamentos(osId);
  const { statusCombo } = useStatusOsCombo();
  const [status, setStatus]   = useState('');
  const [obs, setObs]         = useState('');
  const [garantia, setGarantia]               = useState(false);
  const [aprovadaCliente, setAprovadaCliente] = useState(false);

  const registrar = () => {
    if (!status.trim()) {
      toast.error('Selecione o status');
      return;
    }
    adicionarAcompanhamento(
      { status: status.trim(), obsMudaStatus: obs || null, garantia, aprovadaCliente },
      {
        onSuccess: () => { setStatus(''); setObs(''); setGarantia(false); setAprovadaCliente(false); },
      },
    );
  };

  const atual = acompanhamentos[0];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Status atual: {atual?.status
              ? <Badge className="ml-2">{atual.status}</Badge>
              : <span className="text-muted-foreground text-sm">— sem registro —</span>}
          </CardTitle>
        </CardHeader>
        {!disabled && (
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Novo status <span className="text-destructive">*</span></Label>
              {statusCombo.length > 0 ? (
                <Select value={status || undefined} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue placeholder="Selecione o status..." /></SelectTrigger>
                  <SelectContent>
                    {statusCombo.map((s) => (
                      <SelectItem key={s.id} value={s.descricao}>
                        <span className="inline-flex items-center gap-2">
                          {s.cor && <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: s.cor }} />}
                          {s.descricao}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={status} onChange={(e) => setStatus(e.target.value)}
                  placeholder="Digite o status (cadastre em Status de OS)" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Observação da mudança</Label>
              <Input value={obs} onChange={(e) => setObs(e.target.value)}
                placeholder="Motivo / detalhe" />
            </div>
            <div className="flex items-center gap-6 md:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={garantia} onCheckedChange={(v) => setGarantia(v === true)} />
                Em garantia
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={aprovadaCliente} onCheckedChange={(v) => setAprovadaCliente(v === true)} />
                Aprovada pelo cliente
              </label>
              <Button className="gap-2 ml-auto" onClick={registrar} disabled={isSaving}>
                <Save className="h-4 w-4" />Registrar mudança
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Histórico de status</CardTitle></CardHeader>
        <CardContent>
          {acompanhamentos.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma mudança de status registrada.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead>Garantia</TableHead>
                  <TableHead>Aprov. Cliente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {acompanhamentos.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{new Date(a.dtAlteracao).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="font-medium">{a.status}</TableCell>
                    <TableCell>{a.obsMudaStatus}</TableCell>
                    <TableCell>{a.garantia ? 'Sim' : '—'}</TableCell>
                    <TableCell>{a.aprovadaCliente ? 'Sim' : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <OsOcorrenciasSection osId={osId} disabled={disabled} />
    </div>
  );
}

// ── Seção: Ocorrências (dentro da aba Status) ───────────────────────────────

function OsOcorrenciasSection({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { ocorrencias, adicionarOcorrencia, removerOcorrencia, isSaving } = useOsOcorrencias(osId);
  const [texto, setTexto] = useState('');

  const add = () => {
    if (!texto.trim()) { toast.error('Descreva a ocorrência'); return; }
    adicionarOcorrencia({ ocorrencia: texto.trim() }, { onSuccess: () => setTexto('') });
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Ocorrências</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {!disabled && (
          <div className="flex gap-2">
            <Input value={texto} onChange={(e) => setTexto(e.target.value)}
              placeholder="Registrar nova ocorrência..." />
            <Button className="gap-2" onClick={add} disabled={isSaving}>
              <Plus className="h-4 w-4" />Registrar
            </Button>
          </div>
        )}
        {ocorrencias.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2 text-center">Nenhuma ocorrência registrada.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Data / Hora</TableHead>
                <TableHead>Ocorrência</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {ocorrencias.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="text-muted-foreground text-sm">{o.dataOcorrencia} {o.horaOcorrencia}</TableCell>
                  <TableCell>{o.ocorrencia}</TableCell>
                  <TableCell>
                    {!disabled && (
                      <Button size="sm" variant="ghost" onClick={() => removerOcorrencia(o.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Aba: Tarefas (checklist) ────────────────────────────────────────────────

interface LinhaTarefa {
  key: number;
  id: number;
  descricaoTarefa: string;
  isIniciada: boolean;
  isFinalizada: boolean;
  duracao: number;
  temImagemInicio: boolean;
  temImagemFim: boolean;
}

function OsTarefasTab({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { tarefas, salvarTarefas, isSaving } = useOsTarefas(osId);
  const queryClient = useQueryClient();
  const [linhas, setLinhas] = useState<LinhaTarefa[]>([]);

  useEffect(() => {
    setLinhas(tarefas.map((t) => ({
      key: nextKey(),
      id: t.id,
      descricaoTarefa: t.descricaoTarefa ?? '',
      isIniciada: t.isIniciada,
      isFinalizada: t.isFinalizada,
      duracao: t.duracao,
      temImagemInicio: t.temImagemInicio,
      temImagemFim: t.temImagemFim,
    })));
  }, [tarefas]);

  const add = () => setLinhas((p) => [...p, {
    key: nextKey(), id: 0, descricaoTarefa: '', isIniciada: false, isFinalizada: false,
    duracao: 0, temImagemInicio: false, temImagemFim: false,
  }]);
  const upd = (key: number, campo: keyof LinhaTarefa, valor: string | number | boolean) =>
    setLinhas((p) => p.map((l) => l.key === key ? { ...l, [campo]: valor } : l));
  const rem = (key: number) => setLinhas((p) => p.filter((l) => l.key !== key));

  const salvar = () => {
    salvarTarefas(linhas
      .filter((l) => l.descricaoTarefa.trim() !== '')
      .map((l) => ({
        id: l.id || undefined,
        descricaoTarefa: l.descricaoTarefa,
        isIniciada: l.isIniciada,
        isFinalizada: l.isFinalizada,
        duracao: l.duracao,
      })));
  };

  const refreshLista = () => queryClient.invalidateQueries({ queryKey: ['os-tarefas', osId] });
  const upImg = async (tarefaId: number, slot: 'inicio' | 'fim', file: File) => {
    await ordemServicoService.uploadTarefaImagem(osId, tarefaId, slot, file); refreshLista();
  };
  const rmImg = async (tarefaId: number, slot: 'inicio' | 'fim') => {
    await ordemServicoService.deleteTarefaImagem(osId, tarefaId, slot); refreshLista();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Checklist de tarefas</CardTitle>
          {!disabled && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-2" onClick={add}>
                <Plus className="h-4 w-4" />Adicionar tarefa
              </Button>
              <Button size="sm" className="gap-2" onClick={salvar} disabled={isSaving}>
                <Save className="h-4 w-4" />Salvar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {linhas.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhuma tarefa. Clique em "Adicionar tarefa".
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarefa</TableHead>
                <TableHead className="w-20 text-center">Iniciada</TableHead>
                <TableHead className="w-20 text-center">Concluída</TableHead>
                <TableHead className="w-24 text-right">Duração (h)</TableHead>
                <TableHead className="w-72">Fotos (antes / depois)</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((l) => (
                <TableRow key={l.key}>
                  <TableCell className="min-w-[200px] align-top">
                    <Input value={l.descricaoTarefa} disabled={disabled} className="mt-1"
                      onChange={(e) => upd(l.key, 'descricaoTarefa', e.target.value)} />
                  </TableCell>
                  <TableCell className="text-center align-top">
                    <Checkbox className="mt-3" checked={l.isIniciada} disabled={disabled}
                      onCheckedChange={(v) => upd(l.key, 'isIniciada', v === true)} />
                  </TableCell>
                  <TableCell className="text-center align-top">
                    <Checkbox className="mt-3" checked={l.isFinalizada} disabled={disabled}
                      onCheckedChange={(v) => upd(l.key, 'isFinalizada', v === true)} />
                  </TableCell>
                  <TableCell className="w-24 align-top">
                    <Input type="number" min={0} step={0.5} value={l.duracao} disabled={disabled}
                      className="text-right mt-1"
                      onChange={(e) => upd(l.key, 'duracao', Number(e.target.value))} />
                  </TableCell>
                  <TableCell className="align-top">
                    {l.id > 0 ? (
                      <div className="flex gap-2">
                        <OsImageSlot
                          path={`/api/os/${osId}/tarefas/${l.id}/imagem/inicio`}
                          label="Antes" hasImage={l.temImagemInicio} disabled={disabled}
                          onUpload={(f) => upImg(l.id, 'inicio', f)}
                          onRemove={() => rmImg(l.id, 'inicio')} />
                        <OsImageSlot
                          path={`/api/os/${osId}/tarefas/${l.id}/imagem/fim`}
                          label="Depois" hasImage={l.temImagemFim} disabled={disabled}
                          onUpload={(f) => upImg(l.id, 'fim', f)}
                          onRemove={() => rmImg(l.id, 'fim')} />
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Salve a tarefa para anexar fotos.</span>
                    )}
                  </TableCell>
                  <TableCell className="w-10 align-top">
                    {!disabled && (
                      <Button size="sm" variant="ghost" className="mt-1" onClick={() => rem(l.key)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Seção: Despesas (dentro da aba Financeiro) ──────────────────────────────

interface LinhaDespesa {
  key: number;
  id: number;
  contaDespesasId: number;
  contaContabilId: number;
  documentoOrigem: string;
  historico: string;
  valor: number;
  temImagem: boolean;
}

function OsDespesasSection({ osId, disabled }: { osId: number; disabled: boolean }) {
  const { despesas, salvarDespesas, isSaving } = useOsDespesas(osId);
  const { contas }  = useContasDespesas();
  const { plano }   = usePlanoContas();
  const queryClient = useQueryClient();
  const [linhas, setLinhas] = useState<LinhaDespesa[]>([]);

  useEffect(() => {
    setLinhas(despesas.map((d) => ({
      key: nextKey(),
      id: d.id,
      contaDespesasId: d.contaDespesasId,
      contaContabilId: d.contaContabilId,
      documentoOrigem: d.documentoOrigem ?? '',
      historico: d.historico ?? '',
      valor: d.valor,
      temImagem: d.temImagem,
    })));
  }, [despesas]);

  const add = () => setLinhas((p) => [...p, {
    key: nextKey(), id: 0, contaDespesasId: 0, contaContabilId: 0,
    documentoOrigem: '', historico: '', valor: 0, temImagem: false,
  }]);
  const upd = (key: number, campo: keyof LinhaDespesa, valor: string | number) =>
    setLinhas((p) => p.map((l) => l.key === key ? { ...l, [campo]: valor } : l));
  const rem = (key: number) => setLinhas((p) => p.filter((l) => l.key !== key));

  const total = linhas.reduce((s, l) => s + (l.valor || 0), 0);

  const salvar = () => {
    salvarDespesas(linhas.map((l) => ({
      id: l.id || undefined,
      valor: l.valor,
      contaDespesasId: l.contaDespesasId,
      contaContabilId: l.contaContabilId,
      documentoOrigem: l.documentoOrigem || null,
      historico: l.historico || null,
    })));
  };

  const refreshLista = () => queryClient.invalidateQueries({ queryKey: ['os-despesas', osId] });
  const upImg = async (despesaId: number, file: File) => {
    await ordemServicoService.uploadDespesaImagem(osId, despesaId, file); refreshLista();
  };
  const rmImg = async (despesaId: number) => {
    await ordemServicoService.deleteDespesaImagem(osId, despesaId); refreshLista();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Despesas da OS</CardTitle>
          {!disabled && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-2" onClick={add}>
                <Plus className="h-4 w-4" />Adicionar despesa
              </Button>
              <Button size="sm" className="gap-2" onClick={salvar} disabled={isSaving}>
                <Save className="h-4 w-4" />Salvar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {linhas.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhuma despesa lançada. Clique em "Adicionar despesa".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Conta de Despesa</TableHead>
                  <TableHead className="min-w-[180px]">Conta Contábil</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Histórico</TableHead>
                  <TableHead className="text-right w-28">Valor</TableHead>
                  <TableHead className="w-36">Comprovante</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell className="min-w-[180px]">
                      <Select
                        value={l.contaDespesasId ? String(l.contaDespesasId) : '__none__'}
                        onValueChange={(v) => upd(l.key, 'contaDespesasId', v === '__none__' ? 0 : Number(v))}
                        disabled={disabled}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhuma —</SelectItem>
                          {contas.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <Select
                        value={l.contaContabilId ? String(l.contaContabilId) : '__none__'}
                        onValueChange={(v) => upd(l.key, 'contaContabilId', v === '__none__' ? 0 : Number(v))}
                        disabled={disabled}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhuma —</SelectItem>
                          {plano.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.codigoPlanoConta ? `${c.codigoPlanoConta} — ` : ''}{c.descricaoConta}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input value={l.documentoOrigem} disabled={disabled}
                        onChange={(e) => upd(l.key, 'documentoOrigem', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Input value={l.historico} disabled={disabled}
                        onChange={(e) => upd(l.key, 'historico', e.target.value)} />
                    </TableCell>
                    <TableCell className="w-28 align-top">
                      <Input type="number" min={0} step={0.01} value={l.valor} disabled={disabled}
                        className="text-right mt-1"
                        onChange={(e) => upd(l.key, 'valor', Number(e.target.value))} />
                    </TableCell>
                    <TableCell className="w-36 align-top">
                      {l.id > 0 ? (
                        <OsImageSlot
                          path={`/api/os/${osId}/despesas/${l.id}/imagem`}
                          label="Comprovante" hasImage={l.temImagem} disabled={disabled}
                          onUpload={(f) => upImg(l.id, f)}
                          onRemove={() => rmImg(l.id)} />
                      ) : (
                        <span className="text-xs text-muted-foreground">Salve para anexar.</span>
                      )}
                    </TableCell>
                    <TableCell className="w-10 align-top">
                      {!disabled && (
                        <Button size="sm" variant="ghost" className="mt-1" onClick={() => rem(l.key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-3 flex justify-end text-sm font-semibold">
              <span className="mr-4">Total de despesas:</span>
              <span>{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const OrdemServicoForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user }  = useAuth();
  const queryClient = useQueryClient();

  const { formasPagamento } = useFormasPagamentoCombo();

  // ── Estado ─────────────────────────────────────────────────────────────

  const [vendedorId,     setVendedorId]     = useState<number | null>(null);
  const [clienteId,      setClienteId]      = useState<number | null>(null);
  const [nomeRazao,      setNomeRazao]      = useState('');
  const [cpfCnpj,        setCpfCnpj]        = useState('');
  const [fone,           setFone]           = useState('');
  const [celular,        setCelular]        = useState('');
  const [dtEmissao,      setDtEmissao]      = useState(() => new Date().toISOString().slice(0, 10));
  const [observacao,     setObservacao]     = useState('');
  const [formaPagRecId,  setFormaPagRecId]  = useState<number | null>(null);
  const [parcela,        setParcela]        = useState(1);
  const [diasParcela,    setDiasParcela]    = useState(30);
  const [detalhamentoServico, setDetalhamentoServico] = useState('');
  const [acessoriosServico,   setAcessoriosServico]   = useState('');
  const [observacaoServico,   setObservacaoServico]   = useState('');
  const [tipoOrdem,           setTipoOrdem]           = useState<number>(0);
  const [servicos,       setServicos]       = useState<ItemServico[]>([SERVICO_VAZIO(nextKey())]);
  const [itens,          setItens]          = useState<ItemProduto[]>([]);

  // ── Pré-carga em modo edição ───────────────────────────────────────────

  const { data: os } = useQuery({
    queryKey: ['os-detalhe', id],
    queryFn: () => ordemServicoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (!os) return;
    setVendedorId(os.vendedorId);
    setClienteId(os.clienteId ?? null);
    setNomeRazao(os.nomeRazao ?? '');
    setCpfCnpj(os.cpfCnpj ?? '');
    setFone(os.fone ?? '');
    setCelular(os.celular ?? '');
    setDtEmissao(os.dtEmissao.slice(0, 10));
    setObservacao(os.observacao ?? '');
    setFormaPagRecId(os.formaPagRecId ?? null);
    setParcela(os.parcela ?? 1);
    setDiasParcela(os.diasParcela ?? 30);
    setDetalhamentoServico(os.detalhamentoServico ?? '');
    setAcessoriosServico(os.acessoriosServico ?? '');
    setObservacaoServico(os.observacaoServico ?? '');
    setTipoOrdem(os.tipoOrdem ?? 0);
  }, [os]);

  // ── Totalizadores ──────────────────────────────────────────────────────

  const totalServicos = servicos.reduce(
    (s, i) => s + i.qtdMovimento * i.valorUnitario - i.valorDesconto + i.valorAcrescimo, 0);
  const totalProdutos = itens.reduce(
    (s, i) => s + i.qtdMovimento * i.valorUnitario - i.valorDesconto + i.valorAcrescimo, 0);
  const totalLiquido = totalServicos + totalProdutos;

  // ── Handlers ──────────────────────────────────────────────────────────

  const updateServico = (key: number, campo: keyof ItemServico, valor: string | number) =>
    setServicos((prev) => prev.map((s) => s.key === key ? { ...s, [campo]: valor } : s));

  const removeServico = (key: number) =>
    setServicos((prev) => prev.filter((s) => s.key !== key));

  const updateItem = (key: number, campo: keyof ItemProduto, valor: string | number) =>
    setItens((prev) => prev.map((it) => it.key === key ? { ...it, [campo]: valor } : it));

  const removeItem = (key: number) =>
    setItens((prev) => prev.filter((it) => it.key !== key));

  // ── Salvar ─────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!vendedorId) throw new Error('Vendedor obrigatório');
      const servicosValidos = servicos.filter((s) => s.servicoId !== '');
      if (servicosValidos.length === 0) throw new Error('Adicione pelo menos um serviço');

      const request: CriarOsRequest = {
        vendedorId,
        dtEmissao: new Date(dtEmissao).toISOString(),
        clienteId:     clienteId ?? undefined,
        nomeRazao:     nomeRazao || undefined,
        cpfCnpj:       cpfCnpj   || undefined,
        fone:          fone       || undefined,
        celular:       celular    || undefined,
        observacao:    observacao || undefined,
        formaPagRecId: formaPagRecId ?? undefined,
        parcela:       parcela > 0 ? parcela : undefined,
        diasParcela:   diasParcela > 0 ? diasParcela : undefined,
        detalhamentoServico: detalhamentoServico || undefined,
        acessoriosServico:   acessoriosServico   || undefined,
        observacaoServico:   observacaoServico   || undefined,
        tipoOrdem,
        servicos: servicosValidos.map<OsServicoRequest>((s) => ({
          servicoId:     Number(s.servicoId),
          qtdMovimento:  s.qtdMovimento,
          valorUnitario: s.valorUnitario,
          valorDesconto: s.valorDesconto,
          valorAcrescimo: s.valorAcrescimo,
        })),
        produtos: itens.filter((i) => i.produtoId !== '').map<DavProdutoRequest>((i) => ({
          produtoId:     Number(i.produtoId),
          subProdutoId:  0,
          qtdMovimento:  i.qtdMovimento,
          valorUnitario: i.valorUnitario,
          valorDesconto: i.valorDesconto,
          valorAcrescimo: i.valorAcrescimo,
        })),
      };

      return ordemServicoService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['os'] });
      toast.success('Ordem de Serviço salva com sucesso');
      navigate(`/operacional/os/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  // Atualização dos dados do serviço (detalhamento/acessórios/observação/tipo) em edição.
  const saveServicoMutation = useMutation({
    mutationFn: () => ordemServicoService.update(Number(id), {
      detalhamentoServico: detalhamentoServico || null,
      acessoriosServico:   acessoriosServico   || null,
      observacaoServico:   observacaoServico   || null,
      tipoOrdem,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-detalhe', id] });
      toast.success('Dados do serviço atualizados');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const abrirPdf = async () => {
    if (!id) return;
    try {
      const blob = await ordemServicoService.gerarPdf(Number(id));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const isBaixada = os?.isBaixada ?? false;

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/os')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? `Ordem de Serviço #${os?.numeroDav ?? '...'}` : 'Nova Ordem de Serviço'}
            </h1>
            {isBaixada && (
              <Badge className="bg-yellow-500/10 text-yellow-500 mt-1">Em Andamento</Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/operacional/os')}>
            Cancelar
          </Button>
          {isEditing && (
            <Button variant="outline" className="gap-2" onClick={abrirPdf}>
              <Printer className="h-4 w-4" />Imprimir / PDF
            </Button>
          )}
          {!isBaixada && (
            <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4" />Salvar
            </Button>
          )}
        </div>
      </div>

      {/* Abas */}
      <Tabs defaultValue="identificacao">
        <TabsList className={`grid w-full ${isEditing ? 'grid-cols-10' : 'grid-cols-5'}`}>
          <TabsTrigger value="identificacao" className="gap-1">
            <User className="h-4 w-4" />Identificação
          </TabsTrigger>
          <TabsTrigger value="servicos" className="gap-1">
            <Wrench className="h-4 w-4" />Serviços
          </TabsTrigger>
          <TabsTrigger value="produtos" className="gap-1">
            <Package className="h-4 w-4" />Peças/Produtos
          </TabsTrigger>
          {isEditing && (
            <TabsTrigger value="equipamentos" className="gap-1">
              <HardDrive className="h-4 w-4" />Equipamentos
            </TabsTrigger>
          )}
          {isEditing && (
            <TabsTrigger value="tecnicos" className="gap-1">
              <Users className="h-4 w-4" />Técnicos
            </TabsTrigger>
          )}
          {isEditing && (
            <TabsTrigger value="status" className="gap-1">
              <Activity className="h-4 w-4" />Status
            </TabsTrigger>
          )}
          {isEditing && (
            <TabsTrigger value="tarefas" className="gap-1">
              <ListChecks className="h-4 w-4" />Tarefas
            </TabsTrigger>
          )}
          <TabsTrigger value="financeiro" className="gap-1">
            <CreditCard className="h-4 w-4" />Financeiro
          </TabsTrigger>
          <TabsTrigger value="observacoes" className="gap-1">
            <ClipboardList className="h-4 w-4" />Observações
          </TabsTrigger>
        </TabsList>

        {/* ── Aba Identificação ─────────────────────────────────────────── */}
        <TabsContent value="identificacao">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Vendedor / Responsável <span className="text-destructive">*</span></Label>
                <PessoaSearchCombobox
                  value={vendedorId}
                  onChange={setVendedorId}
                  placeholder="Buscar vendedor..."
                  disabled={isBaixada}
                />
              </div>

              <div className="space-y-2">
                <Label>Data de Abertura</Label>
                <Input type="date" value={dtEmissao}
                  onChange={(e) => setDtEmissao(e.target.value)} disabled={isBaixada} />
              </div>

              <Separator className="md:col-span-2" />

              <div className="space-y-2">
                <Label>Cliente Cadastrado</Label>
                <PessoaSearchCombobox
                  value={clienteId}
                  onChange={setClienteId}
                  placeholder="Buscar cliente..."
                  filter="cliente"
                  disabled={isBaixada}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome / Razão Social</Label>
                  <Input value={nomeRazao} onChange={(e) => setNomeRazao(e.target.value)}
                    placeholder="Nome do cliente" disabled={isBaixada} />
                </div>
                <div className="space-y-2">
                  <Label>CPF / CNPJ</Label>
                  <Input value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)}
                    placeholder="000.000.000-00" disabled={isBaixada} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={fone} onChange={(e) => setFone(e.target.value)}
                  placeholder="(00) 0000-0000" disabled={isBaixada} />
              </div>
              <div className="space-y-2">
                <Label>Celular</Label>
                <Input value={celular} onChange={(e) => setCelular(e.target.value)}
                  placeholder="(00) 00000-0000" disabled={isBaixada} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Serviços ──────────────────────────────────────────────── */}
        <TabsContent value="servicos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Serviços da OS</CardTitle>
                {!isBaixada && (
                  <Button size="sm" variant="outline" className="gap-2"
                    onClick={() => setServicos((p) => [...p, SERVICO_VAZIO(nextKey())])}>
                    <Plus className="h-4 w-4" />Adicionar serviço
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código do Serviço</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Vlr Unit.</TableHead>
                      <TableHead className="text-right">Desconto</TableHead>
                      <TableHead className="text-right">Acréscimo</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {servicos.map((srv) => {
                      const subtotal = srv.qtdMovimento * srv.valorUnitario - srv.valorDesconto + srv.valorAcrescimo;
                      return (
                        <TableRow key={srv.key}>
                          <TableCell className="min-w-[180px]">
                            <Input
                              type="number" min={1}
                              value={srv.servicoId === '' ? '' : srv.servicoId}
                              placeholder="ID do serviço"
                              onChange={(e) => updateServico(srv.key, 'servicoId', Number(e.target.value))}
                              disabled={isBaixada}
                            />
                          </TableCell>
                          <TableCell className="w-20">
                            <Input type="number" min={0.001} step={0.001} value={srv.qtdMovimento}
                              onChange={(e) => updateServico(srv.key, 'qtdMovimento', Number(e.target.value))}
                              className="text-right" disabled={isBaixada} />
                          </TableCell>
                          <TableCell className="w-28">
                            <Input type="number" min={0} step={0.01} value={srv.valorUnitario}
                              onChange={(e) => updateServico(srv.key, 'valorUnitario', Number(e.target.value))}
                              className="text-right" disabled={isBaixada} />
                          </TableCell>
                          <TableCell className="w-24">
                            <Input type="number" min={0} step={0.01} value={srv.valorDesconto}
                              onChange={(e) => updateServico(srv.key, 'valorDesconto', Number(e.target.value))}
                              className="text-right" disabled={isBaixada} />
                          </TableCell>
                          <TableCell className="w-24">
                            <Input type="number" min={0} step={0.01} value={srv.valorAcrescimo}
                              onChange={(e) => updateServico(srv.key, 'valorAcrescimo', Number(e.target.value))}
                              className="text-right" disabled={isBaixada} />
                          </TableCell>
                          <TableCell className="text-right font-medium w-28">
                            {subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </TableCell>
                          <TableCell className="w-10">
                            {!isBaixada && servicos.length > 1 && (
                              <Button size="sm" variant="ghost" onClick={() => removeServico(srv.key)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex justify-end">
                <div className="w-60 text-sm space-y-1">
                  <div className="flex justify-between font-semibold text-base border-t pt-2">
                    <span>Total Serviços:</span>
                    <span>{totalServicos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Peças / Produtos ──────────────────────────────────────── */}
        <TabsContent value="produtos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Peças e Materiais Utilizados</CardTitle>
                {!isBaixada && (
                  <Button size="sm" variant="outline" className="gap-2"
                    onClick={() => setItens((p) => [...p, PRODUTO_VAZIO(nextKey())])}>
                    <Plus className="h-4 w-4" />Adicionar peça
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {itens.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nenhuma peça adicionada. Clique em "Adicionar peça" para incluir materiais.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto / Peça</TableHead>
                          <TableHead className="text-right">Qtd</TableHead>
                          <TableHead className="text-right">Vlr Unit.</TableHead>
                          <TableHead className="text-right">Desconto</TableHead>
                          <TableHead className="text-right">Acréscimo</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itens.map((item) => (
                          <ProdutoLinha
                            key={item.key}
                            item={item}
                            onChange={(campo, valor) => updateItem(item.key, campo, valor)}
                            onRemove={() => removeItem(item.key)}
                            showRemove={itens.length > 0}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <div className="w-60 text-sm">
                      <div className="flex justify-between font-semibold text-base border-t pt-2">
                        <span>Total Peças:</span>
                        <span>{totalProdutos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Separator className="my-4" />
              <div className="flex justify-end text-base font-bold">
                <span className="mr-8">Total Geral (Serv. + Peças):</span>
                <span>{totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Equipamentos (edição) ─────────────────────────────────── */}
        {isEditing && id && (
          <TabsContent value="equipamentos">
            <OsEquipamentosTab osId={Number(id)} disabled={isBaixada} />
          </TabsContent>
        )}

        {/* ── Aba Técnicos (edição) ─────────────────────────────────────── */}
        {isEditing && id && (
          <TabsContent value="tecnicos">
            <OsTecnicosTab osId={Number(id)} disabled={isBaixada} />
          </TabsContent>
        )}

        {/* ── Aba Status / Acompanhamento (edição) ──────────────────────── */}
        {isEditing && id && (
          <TabsContent value="status">
            <OsStatusTab osId={Number(id)} disabled={isBaixada} />
          </TabsContent>
        )}

        {/* ── Aba Tarefas / checklist (edição) ──────────────────────────── */}
        {isEditing && id && (
          <TabsContent value="tarefas">
            <OsTarefasTab osId={Number(id)} disabled={isBaixada} />
          </TabsContent>
        )}

        {/* ── Aba Financeiro ────────────────────────────────────────────── */}
        <TabsContent value="financeiro">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <Select
                  value={formaPagRecId ? String(formaPagRecId) : ''}
                  onValueChange={(v) => setFormaPagRecId(v ? Number(v) : null)}
                  disabled={isBaixada}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {formasPagamento.map((fp) => (
                      <SelectItem key={fp.id} value={String(fp.id)}>{fp.descricao}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Número de Parcelas</Label>
                <Input type="number" min={1} value={parcela}
                  onChange={(e) => setParcela(Number(e.target.value))} disabled={isBaixada} />
              </div>

              <div className="space-y-2">
                <Label>Dias entre Parcelas</Label>
                <Input type="number" min={0} value={diasParcela}
                  onChange={(e) => setDiasParcela(Number(e.target.value))} disabled={isBaixada} />
              </div>

              <Separator className="md:col-span-3" />

              <div className="md:col-span-3 flex justify-end">
                <div className="w-72 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Serviços:</span>
                    <span>{totalServicos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Peças:</span>
                    <span>{totalProdutos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold text-base">
                    <span>Total a Pagar:</span>
                    <span>{totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {isEditing && id && (
            <div className="mt-4">
              <OsDespesasSection osId={Number(id)} disabled={isBaixada} />
            </div>
          )}
        </TabsContent>

        {/* ── Aba Observações ───────────────────────────────────────────── */}
        <TabsContent value="observacoes">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo da Ordem</Label>
                  <Select
                    value={String(tipoOrdem)}
                    onValueChange={(v) => setTipoOrdem(Number(v))}
                    disabled={isBaixada}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Interna</SelectItem>
                      <SelectItem value="1">Externa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observações / Defeito relatado</Label>
                <Textarea
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Descreva o problema/defeito relatado pelo cliente..."
                  rows={4}
                  disabled={isBaixada}
                />
              </div>

              <div className="space-y-2">
                <Label>Detalhamento do serviço realizado / Laudo</Label>
                <Textarea
                  value={detalhamentoServico}
                  onChange={(e) => setDetalhamentoServico(e.target.value)}
                  placeholder="Laudo técnico, diagnóstico e serviço executado..."
                  rows={4}
                  disabled={isBaixada}
                />
              </div>

              <div className="space-y-2">
                <Label>Observação da peça e/ou acessórios</Label>
                <Textarea
                  value={acessoriosServico}
                  onChange={(e) => setAcessoriosServico(e.target.value)}
                  placeholder="Acessórios recebidos junto ao equipamento..."
                  rows={3}
                  disabled={isBaixada}
                />
              </div>

              <div className="space-y-2">
                <Label>Observação geral do serviço</Label>
                <Textarea
                  value={observacaoServico}
                  onChange={(e) => setObservacaoServico(e.target.value)}
                  placeholder="Outras observações internas..."
                  rows={3}
                  disabled={isBaixada}
                />
              </div>

              {isEditing && !isBaixada && (
                <div className="flex justify-end">
                  <Button className="gap-2" variant="outline"
                    onClick={() => saveServicoMutation.mutate()}
                    disabled={saveServicoMutation.isPending}>
                    <Save className="h-4 w-4" />Salvar dados do serviço
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rodapé de ações */}
      {!isBaixada && (
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => navigate('/operacional/os')}>
            Cancelar
          </Button>
          <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save className="h-4 w-4" />Salvar
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrdemServicoForm;
